import { useState } from 'react';
import { fetchExceptions, fetchExceptionsSummary, resolveException } from '@/api/exceptions';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportControls } from '@/components/ui/ExportControls';
import { KpiCard } from '@/components/ui/KpiCard';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { SelectField } from '@/components/ui/SelectField';
import { TextArea } from '@/components/ui/TextArea';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { EXCEPTION_SEVERITY_TONE } from '@/constants/defaults';
import { lastNDaysRange } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatDateTime, formatNumber, humanizeEnum } from '@/lib/format';
import { errorMessageOf } from '@/lib/errors';
import type { ExceptionAction, ExceptionRecord, ExceptionsSummary, Page } from '@/types';

const PAGE_SIZE = 20;

const ACTION_OPTIONS: { value: ExceptionAction; label: string }[] = [
  { value: 'RETRY_REFUND', label: 'Retry refund' },
  // { value: 'RESCHEDULE_PICKUP', label: 'Reschedule pickup' },
  { value: 'MARK_RESOLVED_OFFLINE', label: 'Mark resolved offline' },
  { value: 'DISMISS', label: 'Dismiss' },
];

export function ExceptionsScreen() {
  const [page, setPage] = useState(0);
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));
  const { data: summary } = useAsyncData<ExceptionsSummary | null>(
    (signal) => fetchExceptionsSummary(range, signal),
    [range],
  );
  const { data, errorMessage, isLoading, reload } = useAsyncData<Page<ExceptionRecord> | null>(
    (signal) => fetchExceptions(page, PAGE_SIZE, range, signal),
    [page, range],
  );

  const [resolving, setResolving] = useState<ExceptionRecord | null>(null);
  const [note, setNote] = useState('');
  const [action, setAction] = useState<ExceptionAction>('RETRY_REFUND');
  const [isBusy, setIsBusy] = useState(false);

  async function handleResolve() {
    if (!resolving) {
      return;
    }
    setIsBusy(true);
    try {
      await resolveException(resolving.id, note, action);
      toast.success('Exception resolved');
      setResolving(null);
      setNote('');
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  const exceptions = data?.content ?? [];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Exceptions"
        description="Failures that need a human to resolve."
        actions={
          <>
            <DateRangePicker
              value={range}
              onChange={(nextRange) => {
                setRange(nextRange);
                setPage(0);
              }}
            />
            <ExportControls type="exceptions" filenamePrefix="exceptions" range={range} />
          </>
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="Open" value={formatNumber(summary?.totalOpen)} />
        <KpiCard label="Resolved (30d)" value={formatNumber(summary?.resolvedLast30Days)} />
        <Card>
          <h2 className="text-xs font-medium uppercase tracking-wide text-ink-subtle">By type</h2>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {summary && Object.keys(summary.byType).length > 0 ? (
              Object.entries(summary.byType).map(([type, count]) => (
                <Badge key={type} tone="neutral">
                  {humanizeEnum(type)}: {count}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-ink-muted">None</span>
            )}
          </div>
        </Card>
      </div>

      <ResolvedLog exceptions={summary?.resolvedLast30DaysExceptions?.content ?? []} />

      {isLoading ? (
        <LoadingState message="Loading exceptions…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : exceptions.length === 0 ? (
        <Card>
          <p className="py-6 text-center text-sm text-ink-muted">No open exceptions. 🎉</p>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {exceptions.map((exception) => (
            <Card key={exception.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-ink">{exception.title}</h3>
                    {exception.severity ? (
                      <Badge tone={EXCEPTION_SEVERITY_TONE[exception.severity] ?? 'neutral'}>
                        {exception.severity}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-ink-muted">{exception.description}</p>
                  <p className="mt-1 text-xs text-ink-subtle">
                    {exception.returnId ? `${exception.returnId} · ` : ''}
                    {humanizeEnum(exception.exceptionType)} · {formatDateTime(exception.createdAt)}
                  </p>
                </div>
                <Button size="sm" onClick={() => setResolving(exception)}>
                  Resolve
                </Button>
              </div>
            </Card>
          ))}
          {data && data.totalPages > 0 ? (
            <Pagination
              page={data.page ?? page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}

      <Modal
        isOpen={resolving !== null}
        onClose={() => setResolving(null)}
        title="Resolve exception"
        footer={
          <>
            <Button variant="secondary" onClick={() => setResolving(null)} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={handleResolve} isLoading={isBusy} disabled={!note.trim()}>
              Resolve
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <SelectField
            label="Action taken"
            value={action}
            onChange={(event) => setAction(event.target.value as ExceptionAction)}
            options={ACTION_OPTIONS}
          />
          <TextArea
            label="Resolution note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="What did you do to fix it?"
          />
        </div>
      </Modal>
    </div>
  );
}

function ResolvedLog({ exceptions }: { exceptions: ExceptionRecord[] }) {
  if (exceptions.length === 0) {
    return null;
  }
  return (
    <Card padded={false} className="mb-5 overflow-hidden">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink">Recently resolved</h2>
        <p className="mt-0.5 text-xs text-ink-subtle">Last 30 days</p>
      </div>
      <ul className="divide-y divide-line">
        {exceptions.map((exception) => {
          const actionTaken = exception.metadata?.actionTaken;
          return (
            <li key={exception.id} className="flex items-start gap-4 px-5 py-3.5">
              <span className="mt-0.5 flex-shrink-0 font-mono text-xs text-ink-subtle">
                {formatDateTime(exception.resolvedAt ?? exception.createdAt) || '—'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{exception.title}</span>
                  {exception.severity ? (
                    <Badge tone={EXCEPTION_SEVERITY_TONE[exception.severity] ?? 'neutral'}>
                      {exception.severity}
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs text-ink-subtle">
                  {exception.returnId ? `${exception.returnId} · ` : ''}
                  {humanizeEnum(exception.exceptionType)}
                  {typeof actionTaken === 'string' ? ` · ${humanizeEnum(actionTaken)}` : ''}
                  {exception.resolvedBy ? ` · by ${humanizeEnum(exception.resolvedBy)}` : ''}
                </p>
                {exception.resolutionNote ? (
                  <p className="mt-1 text-sm text-ink-muted">“{exception.resolutionNote}”</p>
                ) : null}
              </div>
              <Badge tone="success">Resolved</Badge>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
